import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Link,
} from '@mui/material';
import { brandColors } from '../theme';

export const EULAPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: { xs: 3, md: 6 } }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 600,
            color: brandColors.indigo,
            mb: 1,
          }}
        >
          End User License Agreement
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              IMPORTANT - READ CAREFULLY
            </Typography>
            <Typography variant="body1" paragraph>
              This End User License Agreement ("Agreement") is a legal agreement between you (either an individual 
              or a single entity operating as a franchise licensee) and Barrett HTL Inc ("Licensor," "we," "us," or "our") 
              for the use of the Licensee Portal and related software applications, including all associated 
              media, printed materials, and electronic documentation (collectively, the "Software").
            </Typography>
            <Typography variant="body1" paragraph>
              BY ACCESSING OR USING THE SOFTWARE, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS AGREEMENT, UNDERSTAND IT, 
              AND AGREE TO BE BOUND BY ITS TERMS. IF YOU DO NOT AGREE TO THE TERMS OF THIS AGREEMENT, DO NOT ACCESS 
              OR USE THE SOFTWARE.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              1. Grant of License
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              1.1 Limited License
            </Typography>
            <Typography variant="body1" paragraph>
              Subject to the terms and conditions of this Agreement and your compliance with the franchise agreement, 
              Licensor grants you a limited, non-exclusive, non-transferable, revocable license to access and use the 
              Software solely for your internal business operations as a franchise licensee.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              1.2 Authorized Users
            </Typography>
            <Typography variant="body1" paragraph>
              You may permit your employees, contractors, and authorized agents ("Authorized Users") to access and use 
              the Software on your behalf, provided that:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">Each Authorized User agrees to be bound by the terms of this Agreement</Typography>
              <Typography component="li" variant="body1">You remain fully responsible for all activities conducted through your account</Typography>
              <Typography component="li" variant="body1">You ensure that Authorized Users comply with all applicable terms and restrictions</Typography>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              1.3 Territory and Scope
            </Typography>
            <Typography variant="body1" paragraph>
              Your use of the Software is limited to the geographic territory specified in your franchise agreement. 
              The license granted herein is solely for supporting franchise operations within your designated territory.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              2. Restrictions on Use
            </Typography>
            <Typography variant="body1" paragraph>
              You agree NOT to:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">
                Copy, modify, distribute, sell, lease, or sublicense the Software or any portion thereof
              </Typography>
              <Typography component="li" variant="body1">
                Reverse engineer, decompile, disassemble, or attempt to derive the source code of the Software
              </Typography>
              <Typography component="li" variant="body1">
                Remove, alter, or obscure any proprietary notices or labels on the Software
              </Typography>
              <Typography component="li" variant="body1">
                Use the Software for any purpose other than your authorized franchise operations
              </Typography>
              <Typography component="li" variant="body1">
                Share access credentials or permit unauthorized access to the Software
              </Typography>
              <Typography component="li" variant="body1">
                Use the Software to compete with Barrett HTL Inc or for the benefit of any competitor
              </Typography>
              <Typography component="li" variant="body1">
                Access or use the Software after termination of your franchise agreement
              </Typography>
              <Typography component="li" variant="body1">
                Use automated systems or software to extract data from the Software without written permission
              </Typography>
              <Typography component="li" variant="body1">
                Interfere with or disrupt the integrity or performance of the Software
              </Typography>
              <Typography component="li" variant="body1">
                Attempt to gain unauthorized access to any portion of the Software or related systems
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              3. Intellectual Property Rights
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              3.1 Ownership
            </Typography>
            <Typography variant="body1" paragraph>
              The Software and all intellectual property rights therein are and shall remain the exclusive property of 
              Barrett HTL Inc and its licensors. This Agreement does not convey to you any ownership interest in or to the 
              Software, but only a limited right of use revocable in accordance with the terms of this Agreement.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              3.2 Trademarks
            </Typography>
            <Typography variant="body1" paragraph>
              Barrett HTL Inc's name, logo, and all related product and service names, design marks, and slogans are 
              trademarks of Barrett HTL Inc. You may not use such marks without the prior written permission of Barrett HTL Inc, 
              except as permitted by your franchise agreement.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              3.3 Feedback
            </Typography>
            <Typography variant="body1" paragraph>
              Any feedback, suggestions, or improvements you provide regarding the Software shall become the property of 
              Barrett HTL Inc, and you hereby assign all rights in such feedback to Barrett HTL Inc without compensation.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              4. Data and Privacy
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              4.1 Data Collection and Use
            </Typography>
            <Typography variant="body1" paragraph>
              Your use of the Software is subject to our Privacy Policy. By using the Software, you consent to the 
              collection, use, and sharing of information as described in the Privacy Policy.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              4.2 Franchise Data
            </Typography>
            <Typography variant="body1" paragraph>
              You acknowledge that Barrett HTL Inc may collect and analyze aggregated data from your use of the Software 
              for the purposes of:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">Improving the Software and services</Typography>
              <Typography component="li" variant="body1">Developing industry benchmarks and analytics</Typography>
              <Typography component="li" variant="body1">Supporting the franchise network</Typography>
              <Typography component="li" variant="body1">Complying with franchise agreement reporting requirements</Typography>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              4.3 Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              You are responsible for maintaining the confidentiality of your account credentials and for all activities 
              that occur under your account. You must immediately notify us of any unauthorized use of your account.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              5. Compliance and Franchise Obligations
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              5.1 Franchise Agreement
            </Typography>
            <Typography variant="body1" paragraph>
              Your rights under this Agreement are contingent upon your compliance with your franchise agreement. 
              Any breach of your franchise agreement may result in immediate termination of this Agreement.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              5.2 System Standards
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to comply with all system standards, operational procedures, and quality requirements established 
              by Barrett HTL Inc for the use of the Software.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              5.3 Reporting Obligations
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to provide accurate and timely information through the Software as required by your franchise 
              agreement, including but not limited to financial reports, operational data, and market analytics.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              6. Updates and Modifications
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              6.1 Software Updates
            </Typography>
            <Typography variant="body1" paragraph>
              Licensor may, in its sole discretion, provide updates, upgrades, or modifications to the Software. 
              You agree to implement such updates as directed to maintain compatibility and security.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              6.2 Changes to Agreement
            </Typography>
            <Typography variant="body1" paragraph>
              Licensor reserves the right to modify this Agreement at any time. We will notify you of material changes 
              through the Software or by email. Your continued use of the Software after such notification constitutes 
              acceptance of the modified terms.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              7. Support and Maintenance
            </Typography>
            <Typography variant="body1" paragraph>
              Technical support and maintenance services are provided in accordance with your franchise agreement and 
              applicable support policies. Support levels and response times may vary based on your franchise tier and 
              subscription level.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              8. Warranties and Disclaimers
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              8.1 Limited Warranty
            </Typography>
            <Typography variant="body1" paragraph>
              Licensor warrants that the Software will perform substantially in accordance with its documentation 
              for a period of ninety (90) days from the date of initial access.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              8.2 Disclaimer of Warranties
            </Typography>
            <Typography variant="body1" paragraph>
              EXCEPT AS EXPRESSLY PROVIDED HEREIN, THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. 
              LICENSOR DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES 
              OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </Typography>
            <Typography variant="body1" paragraph>
              LICENSOR DOES NOT WARRANT THAT THE SOFTWARE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE FROM VIRUSES 
              OR OTHER HARMFUL COMPONENTS.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              9. Indemnification
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to indemnify, defend, and hold harmless Barrett HTL Inc, its affiliates, officers, directors, 
              employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including 
              reasonable attorneys' fees, arising out of or in connection with:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">Your use or misuse of the Software</Typography>
              <Typography component="li" variant="body1">Your violation of this Agreement</Typography>
              <Typography component="li" variant="body1">Your violation of any rights of another party</Typography>
              <Typography component="li" variant="body1">Your violation of any applicable laws or regulations</Typography>
              <Typography component="li" variant="body1">Any content or data you submit through the Software</Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              10. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL LICENSOR BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER 
              INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </Typography>
            <Typography variant="body1" paragraph>
              IN NO EVENT SHALL LICENSOR'S AGGREGATE LIABILITY EXCEED THE AMOUNT PAID BY YOU FOR THE SOFTWARE IN THE 
              TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE LIABILITY.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              11. Term and Termination
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              11.1 Term
            </Typography>
            <Typography variant="body1" paragraph>
              This Agreement is effective upon your first access to the Software and continues until terminated in 
              accordance with this section.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              11.2 Termination
            </Typography>
            <Typography variant="body1" paragraph>
              This Agreement automatically terminates upon:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">Termination or expiration of your franchise agreement</Typography>
              <Typography component="li" variant="body1">Your breach of any term of this Agreement</Typography>
              <Typography component="li" variant="body1">Written notice from either party with 30 days advance notice</Typography>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              11.3 Effect of Termination
            </Typography>
            <Typography variant="body1" paragraph>
              Upon termination:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">All rights granted to you under this Agreement immediately cease</Typography>
              <Typography component="li" variant="body1">You must immediately discontinue all use of the Software</Typography>
              <Typography component="li" variant="body1">You must delete all copies of the Software in your possession</Typography>
              <Typography component="li" variant="body1">Licensor may disable your access to the Software</Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              12. Confidentiality
            </Typography>
            <Typography variant="body1" paragraph>
              You acknowledge that the Software contains proprietary and confidential information of Barrett HTL Inc. 
              You agree to maintain the confidentiality of such information and not disclose it to any third party 
              without prior written consent from Barrett HTL Inc, except as required by law or your franchise agreement.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              13. Export Compliance
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to comply with all applicable export and re-export control laws and regulations, including 
              the Export Administration Regulations maintained by the U.S. Department of Commerce, and trade sanctions 
              maintained by the Treasury Department's Office of Foreign Assets Control.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              14. Governing Law and Dispute Resolution
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              14.1 Governing Law
            </Typography>
            <Typography variant="body1" paragraph>
              This Agreement shall be governed by and construed in accordance with the laws of the jurisdiction specified 
              in your franchise agreement, without regard to its conflict of law provisions.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              14.2 Dispute Resolution
            </Typography>
            <Typography variant="body1" paragraph>
              Any disputes arising out of or relating to this Agreement shall be resolved through the dispute resolution 
              procedures specified in your franchise agreement, which may include mandatory arbitration.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              15. General Provisions
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              15.1 Entire Agreement
            </Typography>
            <Typography variant="body1" paragraph>
              This Agreement, together with your franchise agreement and our Privacy Policy, constitutes the entire 
              agreement between you and Licensor regarding the Software and supersedes all prior agreements and 
              understandings.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              15.2 Severability
            </Typography>
            <Typography variant="body1" paragraph>
              If any provision of this Agreement is held to be invalid or unenforceable, the remaining provisions 
              shall continue in full force and effect.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              15.3 Waiver
            </Typography>
            <Typography variant="body1" paragraph>
              No waiver of any term or condition of this Agreement shall be deemed a further or continuing waiver 
              of such term or condition or any other term or condition.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              15.4 Assignment
            </Typography>
            <Typography variant="body1" paragraph>
              You may not assign or transfer this Agreement or any rights granted hereunder without the prior written 
              consent of Licensor. Licensor may assign this Agreement without restriction.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              15.5 Notice
            </Typography>
            <Typography variant="body1" paragraph>
              All notices under this Agreement shall be in writing and deemed given when delivered personally, sent by 
              confirmed email, or sent by certified or registered mail, return receipt requested, to the addresses 
              specified in your franchise agreement.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              16. Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about this Agreement, please contact us at:
            </Typography>
            <Box sx={{ pl: 3 }}>
              <Typography variant="body1">
                <strong>Barrett HTL Inc</strong>
              </Typography>
              <Typography variant="body1">
                Email: <Link href="mailto:legal@barretthtl.com">legal@barretthtl.com</Link>
              </Typography>
              <Typography variant="body1">
                Support: <Link href="mailto:support@barretthtl.com">support@barretthtl.com</Link>
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 4, p: 3, backgroundColor: 'rgba(1, 30, 65, 0.05)', borderRadius: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              BY USING THE SOFTWARE, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS AGREEMENT, UNDERSTAND IT, AND AGREE TO BE 
              BOUND BY ITS TERMS AND CONDITIONS.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last reviewed: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};